<template>
  <div class="w50 pd-lt-7 chart-box">
    <div class="total-ticket-chart">
      <div class="chart-top">
        <div class="chart-top-lt">
          <div class="chart-tabs">
            <ul class="nav" id="myTab" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link active"
                  id="home-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#home"
                  type="button"
                  role="tab"
                  aria-controls="home"
                  aria-selected="true"
                >
                  {{ constants.TICKET_STATUS_BUTTON }}
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  id="profile-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#profile"
                  type="button"
                  role="tab"
                  aria-controls="profile"
                  aria-selected="false"
                >
                  {{ constants.TESTCASE_EXECUTION_REPORT_BUTTON }}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="chart-top-rt" v-if="!isProjectDetailsMode">
          <div class="chart-select">
            <select class="v1-selectbox" id="jiraWBT"></select>
          </div>
        </div>
      </div>
      <div class="chart-btm tab-content" id="myTabContent">
        <div
          class="tab-pane fade show active"
          id="home"
          role="tabpanel"
          aria-labelledby="home-tab"
        >
          <div class="chart-show-img">
          </div>
        </div>
        <div
          class="tab-pane fade"
          id="profile"
          role="tabpanel"
          aria-labelledby="profile-tab"
        >
          <div class="chart-show-img">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import constants from "@/utils/en-US-Employee.json";

// We will use Chart.js that is already present in the project
import { Chart } from "chart.js/auto";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";

interface Project {
  id: string;
  key: string;
  name: string;
}

interface ProjectStatsItem {
  id: string;
  key: string;
  name: string;
  status_counts: { [key: string]: number };
}

export default defineComponent({
  name: "Chart2",
  data() {
    return {
      constants,
      username: "",
      projects: [] as Project[],
      projectStats: [] as ProjectStatsItem[],
      selectedProjectKey: "all",
      chart: null as any, // profile (Testcase Execution) chart
      homeChart: null as any, // ticket status chart
      isProjectDetailsMode: false,
      _tabHandlers: {
        profile: { el: null as HTMLElement | null, type: "", fn: null as any },
        home: { el: null as HTMLElement | null, type: "", fn: null as any }
      }
    };
  },
  async mounted() {
    this.username = localStorage.getItem("jira_user_username") || "";
    
    // Check if we're in project details mode
    this.isProjectDetailsMode = this.$route.name === 'project-detail';
    
    // If in project details mode, set the project key from route
    if (this.isProjectDetailsMode) {
      this.selectedProjectKey = this.$route.params.projectKey as string;
    }

    // Show spinners while loading initial data
    this.showSpinnerHome();
    this.showSpinnerProfile();

    await this.fetchProjects();
    await this.fetchProjectStats();
    await nextTick();
    
    // Only initialize project select if not in project details mode
    if (!this.isProjectDetailsMode) {
      this.initProjectSelect();
    }
    
    this.injectCanvasAndRender(); // profile tab
    this.injectHomeCanvasAndRender(); // home tab (Ticket Status)

    // Hide spinners after initial render
    this.hideSpinnerHome();
    this.hideSpinnerProfile();

    // Re-render charts when tabs are shown (Bootstrap 5).
    const profileTabBtn = document.getElementById('profile-tab') as HTMLElement | null;
    const homeTabBtn = document.getElementById('home-tab') as HTMLElement | null;

    const onProfileShown = () => {
      setTimeout(() => { this.injectCanvasAndRender(); }, 50);
    };
    const onHomeShown = () => {
      setTimeout(() => { this.injectHomeCanvasAndRender(); }, 50);
    };

    // Prefer Bootstrap shown.bs.tab if available; fallback to click only if BS not available
    const hasBootstrap = typeof window !== 'undefined' && (window as any).bootstrap;
    const profileEvent = hasBootstrap ? 'shown.bs.tab' : 'click';
    const homeEvent = hasBootstrap ? 'shown.bs.tab' : 'click';

    profileTabBtn?.addEventListener(profileEvent as any, onProfileShown as any);
    homeTabBtn?.addEventListener(homeEvent as any, onHomeShown as any);

    this._tabHandlers.profile = { el: profileTabBtn, type: profileEvent, fn: onProfileShown } as any;
    this._tabHandlers.home = { el: homeTabBtn, type: homeEvent, fn: onHomeShown } as any;
  },
  beforeUnmount() {
    try { if (this.chart) { this.chart.destroy(); this.chart = null; } } catch (_) {}
    try { if (this.homeChart) { this.homeChart.destroy(); this.homeChart = null; } } catch (_) {}

    // Also destroy any chart bound to existing canvases if present
    const profileContainer = this.getProfileChartContainer();
    const homeContainer = this.getHomeChartContainer();
    const profileCanvas = profileContainer?.querySelector('canvas') as HTMLCanvasElement | null;
    const homeCanvas = homeContainer?.querySelector('canvas') as HTMLCanvasElement | null;
    try { const c = profileCanvas ? Chart.getChart(profileCanvas) : null; c?.destroy(); } catch (_) {}
    try { const c = homeCanvas ? Chart.getChart(homeCanvas) : null; c?.destroy(); } catch (_) {}

    // Remove tab listeners
    const { profile, home } = this._tabHandlers;
    if (profile.el && profile.fn && profile.type) {
      profile.el.removeEventListener(profile.type as any, profile.fn as any);
    }
    if (home.el && home.fn && home.type) {
      home.el.removeEventListener(home.type as any, home.fn as any);
    }
  },
  methods: {
    async fetchProjects() {
      if (!this.username) return;
      try {
        const resp = await axios.get(`${API_BASE_URL}/jira/projects/${this.username}/`);
        this.projects = resp.data?.projects || [];
      } catch (e) {
        console.error("Failed to fetch projects:", e);
      }
    },

    async fetchProjectStats() {
      if (!this.username) return;
      try {
        const resp = await axios.get(`${API_BASE_URL}/jira/project-stats/${this.username}/`);
        this.projectStats = resp.data?.projects || [];
      } catch (e) {
        console.error("Failed to fetch project stats:", e);
        this.projectStats = [];
      }
    },

    initProjectSelect() {
      const select = document.getElementById("jiraWBT") as HTMLSelectElement | null;
      if (!select) return;

      // Clear existing options and populate with projects
      select.innerHTML = "";
      const allOpt = document.createElement("option");
      allOpt.value = "all";
      allOpt.textContent = "All Projects";
      select.appendChild(allOpt);

      this.projects.forEach(p => {
        const opt = document.createElement("option");
        opt.value = p.key;
        opt.textContent = p.name;
        select.appendChild(opt);
      });

      // Set current selection
      select.value = this.selectedProjectKey;

      // Listen for changes
      select.addEventListener("change", () => {
        this.selectedProjectKey = select.value || "all";
        this.showSpinnerHome();
        this.showSpinnerProfile();
        this.renderChart();
        this.renderHomeChart();
        // Hide quickly after render completes
        setTimeout(() => {
          this.hideSpinnerHome();
          this.hideSpinnerProfile();
        }, 50);
      });
    },

    // Spinner helpers (similar to chart3)
    showSpinnerAt(container: HTMLElement | null) {
      if (!container) return;
      if (container.querySelector('[data-role="chart2-loading"]')) return;

      const spinnerWrap = document.createElement("div");
      spinnerWrap.className = "chart2-loading-container";
      spinnerWrap.setAttribute("data-role", "chart2-loading");

      const spinner = document.createElement("div");
      spinner.className = "chart2-loading-spinner";
      spinner.textContent = "Loading data...";

      spinnerWrap.appendChild(spinner);
      container.appendChild(spinnerWrap);

      // Hide canvas while loading
      const existingCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (existingCanvas) existingCanvas.style.display = 'none';
    },

    hideSpinnerAt(container: HTMLElement | null) {
      if (!container) return;
      const spinner = container.querySelector('[data-role="chart2-loading"]');
      if (spinner) spinner.remove();

      const existingCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (existingCanvas) existingCanvas.style.display = 'block';
    },

    showSpinnerHome() { this.showSpinnerAt(this.getHomeChartContainer()); },
    showSpinnerProfile() { this.showSpinnerAt(this.getProfileChartContainer()); },
    hideSpinnerHome() { this.hideSpinnerAt(this.getHomeChartContainer()); },
    hideSpinnerProfile() { this.hideSpinnerAt(this.getProfileChartContainer()); },

    // Aggregate project status counts for selected/all projects
    getAggregatedStatusCounts() {
      const statusCounts: { [status: string]: number } = {};
      const projectsToUse = this.selectedProjectKey === "all"
        ? this.projectStats
        : this.projectStats.filter(p => p.key === this.selectedProjectKey);

      projectsToUse.forEach(p => {
        Object.entries(p.status_counts || {}).forEach(([status, count]) => {
          statusCounts[status] = (statusCounts[status] || 0) + (count || 0);
        });
      });
      return statusCounts;
    },

    // Map Jira statuses into a test execution-like breakdown
    buildTestExecutionData() {
      const statusCounts = this.getAggregatedStatusCounts();

      const toLower = (s: string) => s.toLowerCase();
      let pass = 0, fail = 0, wip = 0, blocked = 0, todo = 0, other = 0;

      Object.entries(statusCounts).forEach(([name, count]) => {
        const n = toLower(name);
        if (n.includes("pass")) pass += count;
        else if (n.includes("fail")) fail += count;
        else if (n.includes("block")) blocked += count;
        else if (n.includes("progress") || n.includes("wip")) wip += count;
        else if (n.includes("todo") || n === "to do" || n === "open" || n.includes("backlog")) todo += count;
        else other += count;
      });

      const executed = pass + fail + blocked + wip + other; // everything except clear ToDo/Open

      const data = [
        { label: "Pass", value: pass, color: "#7CB342" },
        { label: "Fail", value: fail, color: "#F44336" },
        { label: "WIP", value: wip, color: "#FF9800" },
        { label: "Blocked", value: blocked, color: "#3F51B5" },
        { label: "Executed", value: executed, color: "#9E9E9E" },
      ];

      const total = data.reduce((s, d) => s + d.value, 0);
      if (total === 0) {
        return [
          { label: "No Data", value: 1, color: "#E0E0E0" },
        ];
      }

      return data;
    },

    // Build Ticket Status breakdown from raw status_counts
    buildTicketStatusData() {
      const statusCounts = this.getAggregatedStatusCounts();
      const entries = Object.entries(statusCounts);
      if (entries.length === 0) {
        return [{ label: "No Data", value: 1, color: "#E0E0E0" }];
      }

      const palette = [
        "#4F46E5", "#F59E0B", "#10B981", "#EF4444", "#6366F1",
        "#F97316", "#14B8A6", "#8B5CF6", "#22C55E", "#E11D48"
      ];

      return entries
        .sort((a, b) => b[1] - a[1])
        .map(([label, value], idx) => ({
          label,
          value,
          color: palette[idx % palette.length]
        }));
    },

    getProfileChartContainer(): HTMLElement | null {
      const tab = document.getElementById("profile");
      if (!tab) return null;
      const container = tab.querySelector(".chart-show-img") as HTMLElement | null;
      return container;
    },

    getHomeChartContainer(): HTMLElement | null {
      const tab = document.getElementById("home");
      if (!tab) return null;
      const container = tab.querySelector(".chart-show-img") as HTMLElement | null;
      return container;
    },

    injectCanvasAndRender() {
      const container = this.getProfileChartContainer();
      if (!container) return;

      // Hide static image
      const img = container.querySelector("img") as HTMLImageElement | null;
      if (img) img.style.display = "none";

      // Ensure a wrapper exists to center the canvas
      let wrapper = container.querySelector('[data-role="chart2-center"]') as HTMLDivElement | null;
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.setAttribute('data-role', 'chart2-center');
        wrapper.style.display = 'flex';
        wrapper.style.justifyContent = 'center';
        wrapper.style.alignItems = 'center';
        wrapper.style.width = '100%';
        wrapper.style.minHeight = '360px';
        wrapper.style.padding = '10px 0';
        container.appendChild(wrapper);
      }

      // Ensure a canvas exists
      let canvas = wrapper.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = 420;
        canvas.height = 320;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';
        wrapper.appendChild(canvas);
      }
      // Ensure size if canvas already existed
      canvas.width = 420;
      canvas.height = 320;
      canvas.style.display = 'block';
      canvas.style.margin = '0 auto';

      this.renderChart();
    },

    injectHomeCanvasAndRender() {
      const container = this.getHomeChartContainer();
      if (!container) return;

      // Hide static image
      const img = container.querySelector("img") as HTMLImageElement | null;
      if (img) img.style.display = "none";

      // Ensure a wrapper exists to center the canvas
      let wrapper = container.querySelector('[data-role="chart2-center"]') as HTMLDivElement | null;
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.setAttribute('data-role', 'chart2-center');
        wrapper.style.display = 'flex';
        wrapper.style.justifyContent = 'center';
        wrapper.style.alignItems = 'center';
        wrapper.style.width = '100%';
        wrapper.style.minHeight = '360px';
        wrapper.style.padding = '10px 0';
        container.appendChild(wrapper);
      }

      let canvas = wrapper.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = 420;
        canvas.height = 320;
        canvas.style.display = 'block';
        canvas.style.margin = '40px auto';
        wrapper.appendChild(canvas);
      }
      // Ensure size if canvas already existed
      canvas.width = 420;
      canvas.height = 320;
      canvas.style.display = 'block';
      canvas.style.margin = '40px auto';

      this.renderHomeChart();
    },

    createCenterTextPlugin(totalGetter: () => number, label: string) {
      return {
        id: "centerText",
        afterDraw: (chart: any) => {
          const { ctx, chartArea } = chart;
          if (!ctx || !chartArea) return;
          const total = totalGetter();
          const centerX = (chartArea.left + chartArea.right) / 2;
          const centerY = (chartArea.top + chartArea.bottom) / 2;

          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // Total number
          ctx.fillStyle = "#1f2937";
          ctx.font = "bold 18px sans-serif";
          ctx.fillText(String(total), centerX, centerY - 8);

          // Sub-label
          ctx.fillStyle = "#6b7280";
          ctx.font = "12px sans-serif";
          ctx.fillText(label, centerX, centerY + 12);

          ctx.restore();
        }
      };
    },

    renderChart() {
      const container = this.getProfileChartContainer();
      if (!container) return;
      const canvas = container.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) return;

      // Destroy any existing chart bound to this canvas and instance var
      try { const existing = Chart.getChart(canvas); existing?.destroy(); } catch (_) {}
      if (this.chart) {
        try { this.chart.destroy(); } catch (_) {}
        this.chart = null;
      }

      const series = this.buildTestExecutionData();
      const labels = series.map(s => s.label);
      const values = series.map(s => s.value);
      const colors = series.map(s => s.color);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const centerPlugin = this.createCenterTextPlugin(
        () => values.reduce((s, v) => s + v, 0),
        "Total Tickets"
      );

      this.chart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: "#ffffff",
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "60%",
          plugins: {
            legend: {
              display: true,
              position: "bottom",
            },
            title: {
              display: true,
              text: this.selectedProjectKey === "all" ?
                "Testcase Execution Report - All Projects" :
                `Testcase Execution Report - ${this.selectedProjectKey}`,
              font: { size: 14 }
            },
            tooltip: {
              callbacks: {
                label: function(context: any) {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  return `${label}: ${value}`;
                }
              }
            }
          }
        },
        plugins: [centerPlugin]
      });
    },

    renderHomeChart() {
      const container = this.getHomeChartContainer();
      if (!container) return;
      const canvas = container.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) return;

      // Destroy any existing chart bound to this canvas and instance var
      try { const existing = Chart.getChart(canvas); existing?.destroy(); } catch (_) {}
      if (this.homeChart) {
        try { this.homeChart.destroy(); } catch (_) {}
        this.homeChart = null;
      }

      const series = this.buildTicketStatusData();
      const labels = series.map(s => s.label);
      const values = series.map(s => s.value);
      const colors = series.map(s => s.color);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const centerPlugin = this.createCenterTextPlugin(
        () => values.reduce((s, v) => s + v, 0),
        "Total Tickets"
      );

      this.homeChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: "#ffffff",
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "60%",
          plugins: {
            legend: {
              display: true,
              position: "bottom",
            },
            title: {
              display: true,
              text: this.selectedProjectKey === "all" ?
                "Ticket Status - All Projects" :
                `Ticket Status - ${this.selectedProjectKey}`,
              font: { size: 14 }
            },
            tooltip: {
              callbacks: {
                label: function(context: any) {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  return `${label}: ${value}`;
                }
              }
            }
          }
        },
        plugins: [centerPlugin]
      });
    }
  },
  watch: {
    // Watch for changes in the route parameter when in project details mode
    '$route.params.projectKey': {
      handler(newKey, oldKey) {
        if (this.isProjectDetailsMode && newKey !== oldKey && newKey) {
          this.selectedProjectKey = newKey as string;
          // Re-render charts with new project data
          this.showSpinnerHome();
          this.showSpinnerProfile();
          this.renderChart();
          this.renderHomeChart();
          setTimeout(() => {
            this.hideSpinnerHome();
            this.hideSpinnerProfile();
          }, 50);
        }
      },
      immediate: false
    }
  }
});
</script>

<style>
.chart2-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.chart2-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #007bff;
  font-size: 16px;
}

.chart2-loading-spinner::before {
  content: '';
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: chart2-spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes chart2-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
